import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { EditSerial } from "./EditSerial";
import { useSerials } from "../../hooks/UseSerials";
import { EditEpisodes } from "../episodes/EditEpisodes";
import { EditSerialGenres } from "./EditSerialGenres";
import { EditSerialTranslators } from "./EditSerialTranslators";
import { EditSerialVoiceActors } from "./EditSerialVoiceActors";
import { EditSerialFranchise } from "./EditSerialFranchise";

export function EditSerialPanel(props) {
    const [searchParams] = useSearchParams();
    const [serialId, setSerialId] = useState(searchParams.get('id'));
    const navigate = useNavigate();
    const useSerialsHook = useSerials();
    const user = props.user;

    const secondaryContentRef = useRef(null);

    useEffect(() => {
        if (serialId) {
            useSerialsHook.getById(user.token, serialId);
        }
    }, [user, serialId]);

    useEffect(() => {
        if (serialId && secondaryContentRef.current) {
            const timer = setTimeout(() => {
                secondaryContentRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [serialId]);

    const onBackClicked = () => {
        navigate('/');
    };

    const onSaveClicked = async (serial) => {
        if (serialId) {
            await useSerialsHook.update(user.token, serial);
            alert('✅ Дані успішно оновлено!');
        } else {
            const newSerial = await useSerialsHook.create(user.token, serial);
            setSerialId(newSerial.id);
            navigate(`/serials/edit?id=${newSerial.id}`, { replace: true });
            alert('🚀 Серіал створено! Тепер додайте жанри та серії.');
        }
    };

    const onDeleteClicked = async (serial) => {
        if (window.confirm("Ви впевнені, що хочете видалити цей реліз?")) {
            await useSerialsHook.deleteSerial(user.token, serial);
            navigate(`/`, { replace: true });
        }
    };

    const sectionStyle = {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb'
    };

    const headingStyle = {
        marginTop: 0,
        marginBottom: '20px',
        fontSize: '1.25rem',
        color: '#1f2937',
        borderBottom: '2px solid #f3f4f6',
        paddingBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    };

    return (
        <div style={{ backgroundColor: '#f3f4f6', minHeight: '100vh', padding: '20px 10px' }}>
            {/* Navigation */}
            <div style={{ maxWidth: '900px', margin: '0 auto 20px auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={onBackClicked}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontWeight: '600',
                            color: '#374151',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
                    >
                        ← До адмінки
                    </button>

                    <button
                        onClick={() => navigate('/serials')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '1px solid #e0e7ff',
                            backgroundColor: '#eef2ff',
                            color: '#4f46e5',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#e0e7ff'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#eef2ff'}
                    >
                        📋 До серіалів
                    </button>
                </div>

                <div style={{ textAlign: 'right' }}>
                    <h2 style={{ margin: 0, color: '#111827' }}>
                        {serialId ? 'Редагування релізу' : 'Створення нового релізу'}
                    </h2>
                    <small style={{ color: '#6b7280' }}>ID: {serialId || 'новий'}</small>
                </div>
            </div>

            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                {/* Main Form */}
                <div style={sectionStyle}>
                    <h3 style={headingStyle}>📝 Основна інформація</h3>
                    <EditSerial
                        serial={useSerialsHook.serial}
                        onSaveClicked={onSaveClicked}
                        onDeleteClicked={onDeleteClicked}
                    />
                </div>

                {/* Additional blocks */}
                {serialId && (
                    <div ref={secondaryContentRef}>

                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                            <div style={sectionStyle}>
                                <h3 style={headingStyle}>🏷️ Жанри</h3>
                                <EditSerialGenres user={user} serialId={serialId}/>
                            </div>

                            <div style={sectionStyle}>
                                <h3 style={headingStyle}>✍️ Команда перекладу</h3>
                                <EditSerialTranslators user={user} serialId={serialId}/>
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🎙️ Актори озвучування</h3>
                            <EditSerialVoiceActors user={user} serialId={serialId}/>
                        </div>

                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🔗 Франшиза</h3>
                            <EditSerialFranchise user={user} serialId={serialId}/>
                        </div>

                        <div style={sectionStyle}>
                            <h3 style={headingStyle}>🎬 Епізоди</h3>
                            <EditEpisodes user={user} serialId={serialId}/>
                        </div>

                        {/* FOOTER NAVIGATION */}
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            marginTop: '40px',
                            paddingTop: '30px',
                            borderTop: '2px solid #e5e7eb'
                        }}>
                            <button
                                onClick={onBackClicked}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    backgroundColor: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    color: '#374151',
                                    transition: '0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
                            >
                                ← До адмінки
                            </button>

                            <button
                                onClick={() => {
                                    navigate('/serials');
                                    window.scrollTo(0, 0);
                                }}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    border: '1px solid #e0e7ff',
                                    backgroundColor: '#eef2ff',
                                    color: '#4f46e5',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    transition: '0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#e0e7ff'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#eef2ff'}
                            >
                                📋 Повернутися до серіалів
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}